import { useEffect, useMemo, type ReactNode } from 'react';
import { useMatches, useParams } from '@tanstack/react-router';
import { shortcutSheetKey } from '@paige/capturepoint';
import { useAuth } from '../../auth/AuthBoundary';
import { inShell } from '../../shell/in-shell';
import { runsJobs } from '../../tenant/role';
import { HelpSheet } from './HelpSheet';
import { ShortcutsSheet } from './ShortcutsSheet';
import { TourExplainer } from './TourExplainer';
import { audienceOf } from './explainer-steps';
import { useHelpChoices } from './help-choices';
import { closeHelp, currentHelp, openHelp, useHelpView, type HelpView } from './help-view';

/**
 * Help's sheets, beside every route (gap list C8): whichever is open, for the company it was opened in. Outside a
 * job F1 (and ?, as in the workbench) opens the keys too, so the line "or press F1" is true everywhere; inside one
 * the workbench's own sheet answers F1 and this stays out of its way. Renders nothing while nothing is open.
 */
export function HelpHost(): ReactNode {
  const view = useHelpView();
  const signedIn = useAuth().principal !== null;
  useDashboardF1(signedIn);
  if (!view || !signedIn) return null;
  return <OpenSheet view={view} />;
}

function OpenSheet({ view }: { view: HelpView }): ReactNode {
  const back = useMemo(() => ({ current: view.back ?? null }), [view.back]);
  const choices = useHelpChoices(view.tenantId);
  const principal = useAuth().principal!;
  if (view.sheet === 'shortcuts')
    return <ShortcutsSheet onClose={closeHelp} returnFocusTo={back} />;
  if (view.sheet === 'explainer') {
    const membership = principal.tenants.find((t) => t.id === view.tenantId);
    const audience = audienceOf(membership?.membershipRole, runsJobs(principal, view.tenantId));
    return <TourExplainer audience={audience} onClose={closeHelp} returnFocusTo={back} />;
  }
  return <HelpSheet choices={choices} onClose={closeHelp} returnFocusTo={back} />;
}

/** F1 and ? outside a job: the keys of the review screen, in Paige's copy of the sheet. */
function useDashboardF1(signedIn: boolean): void {
  const inWorkbench = useMatches({ select: (matches) => matches.some((m) => inShell(m.routeId)) });
  const { tenant } = useParams({ strict: false }) as { tenant?: string };
  useEffect(() => {
    if (!signedIn || inWorkbench || !tenant) return undefined;
    const press = (event: KeyboardEvent) => {
      const key = shortcutSheetKey(event);
      if (!key) return;
      // ? is a letter in a dialog's field; F1 is never typed, and the browser's own help is not Paige's.
      if (key === '?' && document.querySelector('[role="dialog"]')) return;
      event.preventDefault();
      // The same key closes it again, as the workbench's F1 does.
      if (currentHelp()?.sheet === 'shortcuts') return closeHelp();
      openHelp(tenant, 'shortcuts', document.activeElement as HTMLElement | null);
    };
    window.addEventListener('keydown', press);
    return () => window.removeEventListener('keydown', press);
  }, [signedIn, inWorkbench, tenant]);
}
